import { useEffect, useRef, type RefObject } from 'react';

/**
 * Esc 로 닫기.
 *
 * 에디터의 모달 네 개(자주 틀리는 이름·자막 추가·GIF·효과음)는 전부 배경 클릭으로는
 * 닫혔지만 **Esc 는 아무 반응이 없었다**(2026-09-11 시나리오 점검에서 발견 — 모달이
 * 열린 채 남아 뒤이은 조작이 전부 막혔다). 모달을 Esc 로 닫는 건 학습이 필요 없는
 * 기본 관습이고, 키보드만 쓰는 사람에게는 유일한 탈출구다.
 *
 * `keydown` 을 문서 단계에서 듣는다 — 모달 안 입력칸에 포커스가 있어도 동작해야 한다.
 * 여러 겹이 떠 있으면 나중에 붙은(=위에 있는) 리스너가 먼저 받도록 `capture` 는 쓰지 않는다.
 */
export function useEscapeToClose(onClose: () => void, active = true): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, active]);
}

/**
 * 모달 안에 포커스를 가둔다.
 *
 * 🔴 없으면 Tab 이 **모달 뒤 화면으로 새어 나간다.** 마우스 사용자는 배경이 어두워
 * 못 누르지만, 키보드 사용자는 보이지 않는 곳으로 포커스가 옮겨가 «지금 어디를
 * 조작하는지» 알 수 없게 된다(2026-09-11 점검: 10번 Tab 중 6번 이상이 모달 밖).
 *
 * 열릴 때 첫 요소로 포커스를 옮기고, 닫힐 때 원래 자리로 돌려준다 — 돌려주지 않으면
 * 모달을 닫은 뒤 Tab 이 문서 맨 앞에서 다시 시작한다.
 */
export function useFocusTrap<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const previously = document.activeElement as HTMLElement | null;
    const SEL =
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const items = () =>
      [...root.querySelectorAll<HTMLElement>(SEL)].filter((el) => el.offsetParent !== null);

    items()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const list = items();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (!active || !root.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previously?.focus?.();
    };
  }, []);
  return ref;
}
